import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { SPACING } from '../../../theme';
import { adjustOpacity } from '../../../theme/utils';

/**
 * ExecutionFilters Component
 *
 * Filter controls for execution list.
 *
 * @param {Object} props
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFilterChange - Handler for filter changes
 */
function ExecutionFilters({ filters, onFilterChange }) {
  const theme = useTheme();

  return (
    <Paper
      sx={{
        p: SPACING.lg,
        mb: 3,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        boxShadow: `0 1px 3px ${adjustOpacity(theme.palette.text.primary, 0.1)}`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          mb: SPACING.md,
        }}
      >
        <FilterIcon
          sx={{ color: theme.palette.primary.main, fontSize: '1.5rem' }}
        />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Filters
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="execution-type-label" shrink>
              Execution Type
            </InputLabel>
            <Select
              data-testid="execution-type-select"
              labelId="execution-type-label"
              value={
                filters.is_experiment === null ? '' : filters.is_experiment
              }
              label="Execution Type"
              onChange={e =>
                onFilterChange(
                  'is_experiment',
                  e.target.value === '' ? null : e.target.value
                )
              }
              displayEmpty
              renderValue={selected => {
                if (
                  selected === '' ||
                  selected === null ||
                  selected === undefined
                ) {
                  return <span style={{ opacity: 0.6 }}>All Executions</span>;
                }
                return selected === true
                  ? 'Experiment Executions'
                  : 'Regular Executions';
              }}
              sx={{
                minWidth: 220,
                '.MuiSelect-select': {
                  paddingRight: '48px !important',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    minWidth: 220,
                    maxHeight: 320,
                    zIndex: 2000,
                    '& .MuiMenuItem-root': {
                      minWidth: 220,
                      width: '100%',
                      padding: '8px 16px',
                      whiteSpace: 'nowrap',
                    },
                  },
                },
              }}
            >
              <MenuItem value="" sx={{ width: '100%' }}>
                All Executions
              </MenuItem>
              <MenuItem value={false} sx={{ width: '100%' }}>
                Regular Executions
              </MenuItem>
              <MenuItem value={true} sx={{ width: '100%' }}>
                Experiment Executions
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              data-testid="status-select"
              value={filters.success === null ? '' : filters.success}
              label="Status"
              onChange={e =>
                onFilterChange(
                  'success',
                  e.target.value === '' ? null : e.target.value
                )
              }
              MenuProps={{
                PaperProps: { sx: { maxHeight: 320, zIndex: 2000 } },
              }}
              sx={{
                minWidth: 160,
                '.MuiSelect-select': {
                  paddingRight: '48px !important',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value={true}>Successful</MenuItem>
              <MenuItem value={false}>Failed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            fullWidth
            label="Template (ID or Name)"
            value={filters.template_id}
            onChange={e => onFilterChange('template_id', e.target.value)}
            placeholder="ID or name (min. 3 characters)"
            inputProps={{ 'data-testid': 'template-id-input' }}
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            fullWidth
            label="Search Execution ID"
            value={filters.search}
            onChange={e => onFilterChange('search', e.target.value)}
            placeholder="(min. 3 characters)"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            inputProps={{ 'data-testid': 'executions-search-input' }}
            size="small"
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

export default ExecutionFilters;
